import { t } from '@lingui/core/macro';
import { useEffect, useMemo, useRef } from 'react';
import { AgenticProcess, QueryFilter, QueryRequest } from '@sdk';
import { useEntitiesQuery } from '@sdk/react/hooks';
import { useWorkerHistory, type WorkerHistoryEntry } from '@src/hooks/useWorkerHistory';
import { toMs } from '@src/utils/process-recency';
import { useMultiTypeSearch } from '@src/components/spotlight/useMultiTypeSearch';
import { terminalProfile } from '@src/components/spotlight/profiles';
import type { SearchResult } from '@src/hooks/use-record-search';
import { partsAroundQuery, partsFromFtsSnippet, type MatchPart } from './matchSnippet';
import { pickHistoryTitle } from '@src/components/entity-execution-panel/history-row';
import { isAllScope, scopeIncludesUser, scopeProjectIds, type ScopeFilter } from '@src/lib/scope-filter';

/**
 * Filters + grouping for the Chats history side-menu. Built on the existing
 * worker-history list (`useWorkerHistory`) — the same data the terminal's
 * HistoryModal and the chat dropdown show. Returns the entries scoped, searched
 * and grouped into the claude.ai-style time buckets.
 *
 * Recency = "last active OR last opened": each entry's sort key is
 * max(transcript `last_active_time`, entity `last_active_at`) — the latter is
 * the server-side `activate` stamp every open fires. The worker-history action
 * is still fetched once (a running agent's op stream must not become a refetch
 * storm); OPEN recency instead arrives live through a watched entity query over
 * the listed sessions' AgenticProcess rows, which also supplies the
 * `agentic_process_id` minted by a first-time open (the heal), so the active
 * row can highlight without a refetch.
 */
export interface ChatHistoryFilters {
  /** Project scope (URL-driven, same model as Assets/Triggers). */
  scope: ScopeFilter;
  /** Free-text search over title / last prompt / project. */
  search: string;
}

/** Session record type (the FTS `record_type`) → the history row's worker. */
const SESSION_RECORD_WORKER: Record<string, WorkerHistoryEntry['worker_type']> = {
  claude_session: 'claude',
  codex_session: 'codex',
  copilot_session: 'copilot',
};
const SESSION_RECORD_TYPES = terminalProfile.allowedEntityTypes ?? [];

/** A multi-word query searches the session CONTENT as a phrase: unquoted, the
 *  index ORs the words and "one cli" ranks every session mentioning "one". */
export function contentSearchQuery(search: string): string {
  const q = search.trim();
  if (q.length < 2) return '';
  return /\s/.test(q) && !q.includes('"') ? `"${q}"` : q;
}

type ProjectRef = Pick<WorkerHistoryEntry, 'project_id' | 'project_name' | 'project_cwd'>;

/** Claude's transcript folder name for a cwd (`/a/b.c` → `-a-b-c`). */
function encodeCwd(cwd: string): string {
  return cwd.replace(/[^A-Za-z0-9-]/g, '-');
}

/** The transcript folder a session record lives in (its `asset_ref` parent). */
function transcriptDir(assetRef: string | undefined): string | null {
  const parts = (assetRef ?? '').split('/');
  return parts.length >= 2 ? parts[parts.length - 2] : null;
}

/** A content hit whose session is not in the loaded history page (older than
 *  the cap) still lists, as a minimal row built from the search result. Its
 *  project comes from the transcript folder, matched against the folders of
 *  the loaded rows — so the navigator's scope still applies to it. */
function entryFromHit(hit: SearchResult, projectByDir: Map<string, ProjectRef>): WorkerHistoryEntry | null {
  const worker_type = SESSION_RECORD_WORKER[hit.record_type];
  if (!worker_type) return null;
  const project = projectByDir.get(transcriptDir(hit.asset_ref) ?? '');
  return {
    worker_type,
    worker_id: hit.record_id,
    project_id: project?.project_id ?? null,
    project_name: project?.project_name ?? null,
    project_cwd: project?.project_cwd ?? null,
    last_active_time: hit.modified_at,
    name: hit.fts_title || hit.name || null,
    last_prompt: null,
    git_branch: null,
    message_count: hit.message_count ?? null,
    agentic_process_id: null,
  };
}

export interface ChatBucket {
  label: string;
  entries: WorkerHistoryEntry[];
}

function processFor(entry: WorkerHistoryEntry): AgenticProcess | null {
  return entry.agentic_process_id
    ? (AgenticProcess.getByIdFromCache<AgenticProcess>(entry.agentic_process_id) ?? null)
    : null;
}

export function isFavorite(entry: WorkerHistoryEntry): boolean {
  return processFor(entry)?.favorite_index != null;
}

/** A worker-history entry passes the project scope. `all` shows everything;
 *  `user` shows only unscoped entries; `project`/`filter` match by project_id
 *  (plus unscoped when the scope includes user). */
function matchesScope(entry: WorkerHistoryEntry, scope: ScopeFilter): boolean {
  if (isAllScope(scope)) return true;
  if (entry.project_id) return scopeProjectIds(scope).includes(entry.project_id);
  return scopeIncludesUser(scope);
}

/**
 * Anti-flicker stability window for the recency sort: two entries whose
 * recency timestamps differ by LESS than this must keep their current relative
 * order (a running session's stream of sub-minute updates must not shuffle the
 * list under the user's cursor). Production: 1 minute. Tests pass a small
 * window (e.g. 1s) via the `sortStabilityMs` parameter of `useChatHistory`.
 */
export const CHAT_SORT_STABILITY_MS = 60_000;

/** "Last active OR last opened": transcript content time vs the entity
 *  `activate` stamp. The third source is NOT a dup of the second: the row
 *  field is the backend/live-query snapshot, while `processFor` reads the
 *  cache the WS merge keeps fresh for entries whose id was already known. */
function tsOf(entry: WorkerHistoryEntry): number {
  return Math.max(toMs(entry.last_active_time), toMs(entry.last_active_at), toMs(processFor(entry)?.last_active_at));
}

/** Group a recency-sorted list into Today / Yesterday / Previous 7|30 days / Older. */
function bucketize(sorted: WorkerHistoryEntry[], ts: (e: WorkerHistoryEntry) => number): ChatBucket[] {
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = 24 * 60 * 60 * 1000;
  const startYesterday = startToday - day;
  const start7 = startToday - 7 * day;
  const start30 = startToday - 30 * day;

  const buckets: ChatBucket[] = [
    { label: t`Today`, entries: [] },
    { label: t`Yesterday`, entries: [] },
    { label: t`Previous 7 days`, entries: [] },
    { label: t`Previous 30 days`, entries: [] },
    { label: t`Older`, entries: [] },
  ];
  for (const entry of sorted) {
    const t = ts(entry);
    if (t >= startToday) buckets[0].entries.push(entry);
    else if (t >= startYesterday) buckets[1].entries.push(entry);
    else if (t >= start7) buckets[2].entries.push(entry);
    else if (t >= start30) buckets[3].entries.push(entry);
    else buckets[4].entries.push(entry);
  }
  return buckets.filter((b) => b.entries.length > 0);
}

export function useChatHistory(
  filters: ChatHistoryFilters,
  limit = 50,
  /** Recency deltas smaller than this window must not reorder rows (see
   *  {@link CHAT_SORT_STABILITY_MS}); tests shrink it to keep runs fast. */
  sortStabilityMs: number = CHAT_SORT_STABILITY_MS,
) {
  // When scoped to one-or-more projects, push the project_ids to the backend so
  // the per-project cap is computed there — otherwise an under-active project's
  // sessions never make it into the response to be filtered client-side.
  const projectIds = isAllScope(filters.scope) ? undefined : scopeProjectIds(filters.scope);
  // Empty chats are filtered at the source; `fetchedCount` is the pre-filter length.
  const { entries, fetchedCount, isLoading, refetch } = useWorkerHistory(limit, {
    projectIds: projectIds?.length ? projectIds : undefined,
  });

  // Quick search also looks INSIDE sessions (the backend FTS over session
  // records): the history row only carries the title and the LAST prompt, so a
  // word from an earlier turn would otherwise never match. Debounced, and
  // UNSCOPED on the wire: the index files session records under the user, so a
  // project filter there drops every session — the scope is applied below, per
  // row, like the local matches.
  const contentQuery = contentSearchQuery(filters.search);
  const {
    results: contentHits,
    isLoading: contentLoading,
    answeredQuery,
  } = useMultiTypeSearch(contentQuery, SESSION_RECORD_TYPES, null);
  const isSearchingContent = !!contentQuery && (contentLoading || answeredQuery !== contentQuery.trim());

  // Live AgenticProcess rows for the LISTED sessions only (`session_id $IN`):
  // an open of a never-materialized session mints its entity (the heal) and
  // every open re-stamps `last_active_at` — the watched query streams both in
  // without refetching worker-history (no op-storm; membership is bounded by
  // the visible list).
  const sessionIdsKey = useMemo(
    () =>
      entries
        .map((e) => e.worker_id)
        .filter(Boolean)
        .sort()
        .join(','),
    [entries],
  );
  const processQuery = useMemo(
    () =>
      new QueryRequest({
        type: AgenticProcess.type,
        scope: [],
        name: `chatHistoryProcesses:${sessionIdsKey.split(',').length}sessions`,
        // $OR of $EQ (not $IN): the client-side data_op re-validator only
        // evaluates $IN in its array-field ($PROP) form, so a scalar-field $IN
        // would silently drop live membership updates — the whole point here.
        query: new QueryFilter({
          match: {
            op: '$OR',
            operands: sessionIdsKey.split(',').map((sid) => ({ op: '$EQ', operands: ['session_id', sid] })),
          },
        }),
      }),
    [sessionIdsKey],
  );
  const { data: liveProcesses } = useEntitiesQuery<AgenticProcess>(processQuery, {
    enabled: sessionIdsKey.length > 0,
  });

  // In-place UPDATE ops on member rows (the `activate` stamp) re-notify the
  // watched query (store.ts onDataOp update reconcile), so `liveProcesses` is
  // the single live signal — no per-entity subscription bridge needed.

  // Anti-flicker memory: worker_id → rank of the PREVIOUSLY RENDERED order.
  // Committed in an effect (not inside the memo) so the memo stays pure under
  // StrictMode/concurrent double-invocation.
  const prevRankRef = useRef<Map<string, number>>(new Map());

  const { buckets, order, matches } = useMemo(() => {
    const bySession = new Map<string, AgenticProcess>();
    for (const p of liveProcesses ?? []) {
      if (p.session_id) bySession.set(p.session_id, p);
    }

    const q = filters.search.trim().toLowerCase();
    // Content hits count only while the query they answer is still typed.
    const hitIds = new Set(q ? contentHits.map((h) => h.record_id) : []);
    const known = new Set(entries.map((e) => e.worker_id));
    const projectByDir = new Map<string, ProjectRef>();
    for (const e of entries) {
      if (e.project_cwd && e.project_id) projectByDir.set(encodeCwd(e.project_cwd), e);
    }
    const extra = q
      ? contentHits
          .filter((h) => !known.has(h.record_id))
          .map((h) => entryFromHit(h, projectByDir))
          .filter((e): e is WorkerHistoryEntry => e != null)
      : [];
    const filtered = [...entries, ...extra]
      .map((e) => {
        // Reconcile the row with its live entity: a first-time open minted the
        // AgenticProcess AFTER this list was fetched — adopt its id (the
        // highlight key) and its open stamp.
        const live = bySession.get(e.worker_id);
        if (!live) return e;
        return {
          ...e,
          agentic_process_id: e.agentic_process_id ?? live.id,
          last_active_at: Math.max(toMs(e.last_active_at), toMs(live.last_active_at)) || null,
        };
      })
      .filter((e) => {
        if (!matchesScope(e, filters.scope)) return false;
        if (q && !hitIds.has(e.worker_id)) {
          const title = pickHistoryTitle(processFor(e), e);
          const hay = `${title} ${e.last_prompt ?? ''} ${e.project_name ?? ''}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      });

    // Each entry's recency is invariant within one recompute — resolve it once
    // (tsOf does a cache lookup) instead of per comparator call.
    const recency = new Map(filtered.map((e) => [e.worker_id, tsOf(e)]));
    const ts = (e: WorkerHistoryEntry) => recency.get(e.worker_id) ?? 0;

    // Recency sort with the stability window: an over-window delta orders by
    // time; an under-window delta keeps the previously rendered relative order
    // (both ranks known), so live sessions don't shuffle under the cursor.
    const prevRank = prevRankRef.current;
    const sorted = [...filtered].sort((a, b) => {
      const delta = ts(b) - ts(a);
      if (Math.abs(delta) >= sortStabilityMs) return delta;
      const ra = prevRank.get(a.worker_id);
      const rb = prevRank.get(b.worker_id);
      if (ra != null && rb != null && ra !== rb) return ra - rb;
      return delta;
    });
    // Where each hit matched (search only): the last prompt, else the content
    // snippet from the index. A title-only match is already visible in the
    // title, so its line shows the last prompt as plain context instead.
    const matches = new Map<string, MatchPart[]>();
    if (q) {
      const snippetById = new Map(contentHits.map((h) => [h.record_id, h.snippet]));
      for (const e of sorted) {
        const parts =
          partsAroundQuery(e.last_prompt, q) ??
          partsFromFtsSnippet(snippetById.get(e.worker_id)) ??
          (e.last_prompt ? [{ text: e.last_prompt.replace(/\s+/g, ' '), mark: false }] : null);
        if (parts) matches.set(e.worker_id, parts);
      }
    }
    return { buckets: bucketize(sorted, ts), order: sorted.map((e) => e.worker_id), matches };
  }, [entries, contentHits, filters, liveProcesses, sortStabilityMs]);

  useEffect(() => {
    prevRankRef.current = new Map(order.map((id, i) => [id, i]));
  }, [order]);

  const total = useMemo(() => buckets.reduce((n, b) => n + b.entries.length, 0), [buckets]);

  // `total` is what is displayed; `fetchedCount` is pre-filter, for paging.
  return { buckets, matches, total, fetchedCount, isLoading, isSearchingContent, refetch };
}
